import {
  isVectorSource,
  type SourceDescriptor,
  type VectorFeature,
  type VectorSourceDescriptor,
} from '../data/model';
import type { LayerDefinition, LayerId } from '../project/model';
import { evaluateAttributeRule } from '../rules/evaluate';
import { selectedFeatureRef, type SelectedFeatureRef, type SelectionMutation } from './model';

/** Canonical geometry access for application actions, independent of rendering and joined properties. */
export function canonicalSelectedFeatures(
  sources: readonly SourceDescriptor[],
  layers: readonly LayerDefinition[],
  selection: readonly SelectedFeatureRef[],
): readonly { readonly ref: SelectedFeatureRef; readonly feature: VectorFeature }[] {
  const sourcesById = new Map(sources.filter(isVectorSource).map((source) => [source.id, source]));
  const layersById = new Map(layers.map((layer) => [layer.id, layer]));
  const seen = new Set<string>();
  return selection.flatMap((ref) => {
    const source = sourcesById.get(ref.sourceId);
    const layer = layersById.get(ref.layerId);
    const feature = source?.data.features[ref.sourceFeatureIndex];
    if (
      !source ||
      !layer ||
      layer.sourceId !== source.id ||
      !feature ||
      seen.has(ref.key) ||
      selectedFeatureRef(layer.id, source.id, feature, ref.sourceFeatureIndex).key !== ref.key
    )
      return [];
    seen.add(ref.key);
    return [{ ref, feature }];
  });
}

export function selectedFeaturesForLayer(
  selection: readonly SelectedFeatureRef[],
  layerId: LayerId,
): readonly SelectedFeatureRef[] {
  return selection.filter((feature) => feature.layerId === layerId);
}

export function addSelectedFeature(
  selection: readonly SelectedFeatureRef[],
  feature: SelectedFeatureRef,
): readonly SelectedFeatureRef[] {
  return selection.some((candidate) => candidate.key === feature.key)
    ? selection
    : [...selection, feature];
}

export function toggleSelectedFeature(
  selection: readonly SelectedFeatureRef[],
  feature: SelectedFeatureRef,
): readonly SelectedFeatureRef[] {
  return selection.some((candidate) => candidate.key === feature.key)
    ? selection.filter((candidate) => candidate.key !== feature.key)
    : [...selection, feature];
}

/** Applies one canonical set operation without repeated linear membership scans. */
export function applySelectionMutation(
  selection: readonly SelectedFeatureRef[],
  features: readonly SelectedFeatureRef[],
  mutation: SelectionMutation,
): readonly SelectedFeatureRef[] {
  const unique = new Map(features.map((feature) => [feature.key, feature]));
  if (mutation === 'replace') return [...unique.values()];
  if (unique.size === 0) return selection;

  const current = new Map(selection.map((feature) => [feature.key, feature]));
  if (mutation === 'remove') {
    let changed = false;
    for (const key of unique.keys()) changed = current.delete(key) || changed;
    return changed ? [...current.values()] : selection;
  }
  if (mutation === 'toggle') {
    for (const [key, feature] of unique) {
      if (!current.delete(key)) current.set(key, feature);
    }
    return [...current.values()];
  }

  let changed = false;
  for (const [key, feature] of unique) {
    if (current.has(key)) continue;
    current.set(key, feature);
    changed = true;
  }
  return changed ? [...current.values()] : selection;
}

export function removeSelectedFeaturesForLayer(
  selection: readonly SelectedFeatureRef[],
  layerId: LayerId,
): readonly SelectedFeatureRef[] {
  const next = selection.filter((feature) => feature.layerId !== layerId);
  return next.length === selection.length ? selection : next;
}

export function pruneSelectedFeaturesForDisplayFilter(
  selection: readonly SelectedFeatureRef[],
  layer: LayerDefinition,
  source: VectorSourceDescriptor,
): readonly SelectedFeatureRef[] {
  let changed = false;
  const next = selection.filter((selected) => {
    if (selected.layerId !== layer.id) return true;
    const feature = source.data.features[selected.sourceFeatureIndex];
    const keep =
      selected.sourceId === source.id &&
      feature !== undefined &&
      evaluateAttributeRule(layer.displayFilter, feature.properties);
    if (!keep) changed = true;
    return keep;
  });
  return changed ? next : selection;
}
